import React, { useEffect, useState, useRef } from 'react';
import Footer from '@components/Footer/Footer';
import {
    selectGetSelectedLoanNumber,
    selectLoan,
    selectMainMenuOptions,
    selectMobileWidget,
    selectUserInfo,
    selectNavigationOptions,
    selectOnboardingWidgets,
    selectServicingIdentifiers,
    selectOnboardingCards
} from '@store/selectors/widgets.selectors';
import { CenterContentContainer, MainContentWrapper } from '@app.styled';
import { auth, config } from '../config';
import { I18nextProvider } from 'react-i18next';
import { generateTheme } from '@fmc-digital/ui';
import { ThemeProvider } from '@mui/material';
import Header from './Header';
import { useSelector, useDispatch } from 'react-redux';
import PromotionWidget from '@components/PromotionWidget/PromotionWidget';
import WidgetsContainer from './WidgetsContainer';
import WelcomeBar from '@components/WelcomeBar/WelcomeBar';
import { Identifier, MainMenuOptions, NavigationOptions, Notifications, SiteMessageRequest, WidgetTemplate } from '@types';
import { SelectNotificationsData } from '@store/selectors/alerts.selectors';
import { CodingConstants, PromotionWidgetData } from '@constants/CodingConstants';
import { fetchAlertAsync } from '@store/actions/alerts.actions';
import { selectServicingError } from '@store/errors/selectors';
import { fetchPayHistoryAsync } from '@store/actions/payHistory.action';
import { fetchAutoPayStatus } from '@store/actions/autoPayStatus.action';
import { OnBoardingContainer, OnboardingActiveCards, i18n } from '@fmc-digital/servicing';
import { setAdditionalProperties, activityEventUpdate } from 'fmc-digital-common';
import { getOnboardingAppFeatureFlags, getDurationsInDays } from '@store/reducers/config.reducer';
import { localStorageHelper } from '@utils/localStorageHelpder';
import { fetchWidgetRefreshAsync } from '@store/actions/widgets.actions';
import { logApiEvent, logUserEvent } from '@analytics/amplifyLogging';
import { storeMigrationData } from '@utils/onBoardingHelper';
const MainContainer: React.FunctionComponent<any> = ({ userName, displayPage }) => {
    const dispatch = useDispatch();
    const { getItem } = localStorageHelper;
    const mobileWidget = useSelector(selectMobileWidget);
    const loanList: Identifier[] = useSelector(selectLoan);
    const servicingIdenfiers: Identifier[] = useSelector(selectServicingIdentifiers);
    const alerts: Notifications[] = useSelector(SelectNotificationsData);
    const menuList: MainMenuOptions[] = useSelector(selectMainMenuOptions);

    const navigationList: NavigationOptions[] = useSelector(selectNavigationOptions);
    const errors = useSelector(selectServicingError);
    const selectedLoan = useSelector(selectGetSelectedLoanNumber);
    const onboardingFeatureFlags = useSelector(getOnboardingAppFeatureFlags);
    const onboardingCardsList = useSelector(selectOnboardingCards(onboardingFeatureFlags, 'welcome-onboarding'));
    const durationsInDays = useSelector(getDurationsInDays);

    const token = auth.getOkta().getAccessToken();
    const userInfo = useSelector(selectUserInfo);
    // Make use of "setContnet" to switch between Dashboard and New Loan in progress page
    const [loanType, setLoanType] = useState(selectedLoan ? selectedLoan.type : loanList[0]?.type);
    const [loanNumber, setLoanNumber] = useState(selectedLoan ? selectedLoan.identifier : loanList[0]?.identifier);
    const [defaultOption, setDefaultOption] = useState(selectedLoan ? selectedLoan : loanList[0]);
    const [propertyState, setPropertyState] = useState(
        selectedLoan ? selectedLoan.propertyAddress?.state : loanList[0]?.propertyAddress.state
    );
    const [showOnboarding, setShowOnboarding] = useState(true);

    const onRedirectToDashboard = () => {
        setShowOnboarding(false);

        dispatch(fetchWidgetRefreshAsync.request('?refresh=true'));
    };

    const onError = () => {
        // setError(true); // Need to be changed when mfa api integration completed
        console.log('account lock error ');
    };

    const getOption = (options: Identifier) => {
        setAdditionalProperties('amplify', { loanNumber: options.identifier });
        setLoanType(options.type);
        setLoanNumber(options.identifier);
        setDefaultOption(options);
        setPropertyState(options.propertyAddress.state);
    };
    useEffect(() => {
        if (errors) {
            const loan = loanList.find(i => i.type == CodingConstants.inProgressLoanType);
            setLoanType(loan.type);
            setLoanNumber(loan.identifier);
            setDefaultOption(loan);
        }
    }, [errors]);

    const initialized = useRef(false);

    useEffect(() => {
        if (!initialized.current) {
            initialized.current = true;

            const url = config.env('activityUpdateUrl');
            storeMigrationData(loanNumber, url);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (loanNumber) {
            setAdditionalProperties('amplify', { loanNumber: loanNumber });
            if (loanType === CodingConstants.servicingLoanType) {
                dispatch(fetchPayHistoryAsync.request(loanNumber));
                dispatch(fetchAutoPayStatus.request(loanNumber));
            }
        }
    }, [loanNumber]);

    useEffect(() => {
        if (Array.isArray(servicingIdenfiers) && servicingIdenfiers.length) {
            const siteMessageRequest: SiteMessageRequest = { accountLoanInfo: [] };
            servicingIdenfiers.forEach(account => {
                siteMessageRequest.accountLoanInfo.push({ accountNumber: account.identifier, loanType: account.loanType || '' });
            });
            if (!alerts) {
                dispatch(fetchAlertAsync.request(siteMessageRequest));
            }
        }
    }, [servicingIdenfiers]);

    const updateLoanType = (content: string) => {
        setLoanType(content);
    };

    const updateActivityEvent = (activityData: any) => {
        const url = config.env('activityUpdateUrl');
        activityEventUpdate(activityData, url);
    };

    return showOnboarding && onboardingCardsList?.length > 0 ? (
        <I18nextProvider i18n={i18n}>
            <ThemeProvider theme={generateTheme()}>
                <OnBoardingContainer
                    cards={onboardingCardsList}
                    returnToDashboard={onRedirectToDashboard}
                    onError={onError}
                    mobileNumber={userInfo.mobile}
                    showBackdrop={true}
                    metadata={{
                        tcpaUrl: config.env('tcpaUrl'),
                        paperlessUrl: config.env('paperlessUrl'),
                        triggerMfaUrl: config.env('triggerMfaUrl'),
                        validateMfaUrl: config.env('validateMfaUrl'),
                        accessToken: token,
                        loanNumber: loanNumber,
                        xApiKey: config.env('xApiKey'),
                        userName: userName
                    }}
                    localStorageHelper={localStorageHelper}
                    // userId={userInfo.userUUID}
                    durationInDays={durationsInDays}
                    logUserEvent={logUserEvent}
                    logApiEvent={logApiEvent}
                    showMFA={onboardingFeatureFlags['onboardingWithMFA']}
                    updateActivityEvent={updateActivityEvent}
                />
            </ThemeProvider>
        </I18nextProvider>
    ) : (
        <MainContentWrapper id="home">
            <Header
                loanOptions={loanList}
                getOption={getOption}
                name={userName}
                navigationOptions={navigationList}
                menuOptions={menuList}
                loanType={updateLoanType}
                content={loanType}
            />
            <CenterContentContainer id="contentContainer">
                <WelcomeBar
                    name={userName}
                    loanOptions={loanList}
                    getOption={getOption}
                    defaultOption={defaultOption}
                    content={loanType}
                    updateLoanType={updateLoanType}
                />

                <WidgetsContainer
                    loanNumber={loanNumber}
                    content={loanType}
                    updateLoanType={updateLoanType}
                    getOption={getOption}
                    propertyState={propertyState}
                    userName={userName}
                    displayPage={displayPage}
                />
            </CenterContentContainer>

            {mobileWidget && PromotionWidgetData.includes(loanType) && <PromotionWidget component={mobileWidget.id} />}
            <Footer />
        </MainContentWrapper>
    );
};

export default React.memo(MainContainer);
